import { React, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import urls, { getCsrfToken } from '../com.zedock.common.script/urls.js';
import { clicks, useHandleAddToCart } from '../com.zedock.common.script/common.js';

import '../com.zedock.common.style/Login.css';
import '../com.zedock.common.style/common.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [loginMode, setLoginMode] = useState("otp"); // "password" | "otp"
  const [name, setUsername] = useState('');
  const [pwd, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const total = location.state?.totalPrice;
  const addToCartFunc = useHandleAddToCart();
  const allowedRedirects = ['/SignUp', '/ForgotPassword'];
  const switchMode = (mode) => {
    setLoginMode(mode);
    setError('');
    setSuccess('');
    setOtpSent(false);
    setOtp('');
  };

  useEffect(() => {
    clicks(location.pathname);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    if (otpTimer <= 0) return;
    const timer = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [otpTimer]);

  // ---------- password login ----------
  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setSuccess('');


    try {
      const response = await fetch(`${urls.BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-XSRF-TOKEN": getCsrfToken()
        },
        body: new URLSearchParams({ name, pwd }),
        credentials: "include"
      });

      if (response.ok) {
        const products = await response.json();
        localStorage.setItem('userName', products.username);
        setSuccess("Login Successful");
        handleCookies();
        if (allowedRedirects.includes(from))
          navigate('/Product', { replace: true });
        else
          navigate(from, { replace: true });
      } else {
        setError("Invalid email or password. Please try again.");
      }
    } catch (err) {
      setError(err.message);
      console.error('Login error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCookies = async () => {

    try {
      const expressionProduct = document.cookie.match(new RegExp('(^| )' + 'products' + '=([^;]+)'));
      const productIds = expressionProduct[0]
        .split('products=')[1]
        .replace(/,$/, '')
        .split(',')

      productIds.map((item) => {
        addToCartFunc(item.split('-')[0], item.split('-')[1])
      });
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      document.cookie = "products=; path=/; max-age=0";
    }
  }

  // ---------- otp login: step 1, send otp ----------
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${urls.BASE_URL}/login/loginByOTP`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          "X-XSRF-TOKEN": getCsrfToken()
        },
        body: JSON.stringify({ email: name }),
        credentials: "include"
      });

      if (response.ok) {
        setOtpSent(true);
        setOtpTimer(30);
        setSuccess("OTP sent to your email.");
      } else {
        setError("Could not send OTP. Please check your email and try again.");
      }
    } catch (err) {
      setError(err.message);
      console.error('Send OTP error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container px-0">
            <div className="zd-wrap p-2 p-lg-4">

        <div className="row g-0">
          <nav aria-label="breadcrumb" className="zd-crumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <a href="" onClick={(e) => { e.preventDefault(); navigate("/Product") }}>Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Log In</li>
            </ol>
          </nav>
        </div>

        <div className="row justify-content-center  g-2 mb-2">
          <div className="col-12 col-lg-10">
            <div className="zd-auth-shell row g-0">

              {/* image side */}
              <div className="col-12 col-lg-6 d-none d-lg-block">
                <div className="zd-auth-visual h-100">
                  <img
                    src={urls.CDN_URL + 'common/logsign.webp'}
                    alt="Zedock fashion" />
                  <div className="zd-auth-visual-caption">
                    <span className="eyebrow">Welcome back</span>
                    <h3>"Pick up right where you left off — your saved items are waiting."</h3>
                  </div>
                </div>
              </div>

              {/* form side */}
              <div className="col-12 col-lg-6">
                <div className="zd-auth-form">
                  <span className="zd-auth-eyebrow">Zedock account</span>
                  <h2>Log in</h2>
                  <p className="zd-auth-sub">Welcome back. Enter your details to continue.</p>

                  {/* mode toggle */}
                  <div className="zd-login-toggle d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className={`btn btn-sm ${loginMode === "password" ? "btn-dark" : "btn-outline-dark"}`}
                      onClick={() => switchMode("password")}
                    >
                      Password
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${loginMode === "otp" ? "btn-dark" : "btn-outline-dark"}`}
                      onClick={() => switchMode("otp")}
                    >
                      Login with OTP
                    </button>
                  </div>

                  {error && <div className="alert alert-danger py-2">{error}</div>}
                  {success && <div className="alert alert-success py-2">{success}</div>}

                  {/* ---------- password form ---------- */}
                  {loginMode === "password" && (
                    <form onSubmit={handleLogin}>
                      <div className="zd-field">
                        <label htmlFor="exampleInputEmail1">Email ID</label>
                        <input
                          type="text"
                          className="form-control"
                          id="exampleInputEmail1"
                          placeholder="you@example.com"
                          required
                          value={name}
                          onChange={(e) => setUsername(e.target.value)}
                        />
                      </div>

                      <div className="zd-field">
                        <label htmlFor="exampleInputPassword1">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="exampleInputPassword1"
                          placeholder="Password"
                          required
                          value={pwd}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </div>

                      <div className="zd-row-between">
                        <div className="zd-remember">
                          <input type="checkbox" id="exampleCheck1" />
                          <label htmlFor="exampleCheck1">Remember me</label>
                        </div>
                        <button type="button" className="zd-forgot" onClick={() => navigate("/ForgotPassword")}>
                          Forgot password?
                        </button>
                      </div>

                      <button type="submit" className="zd-submit" disabled={submitting}>
                        {submitting ? "Logging in..." : "Log In"}
                      </button>


                      <p className="zd-alt-action">
                        New to Zedock? <a href="" onClick={(e) => { e.preventDefault(); navigate("/SignUp") }}>Create an account</a>
                      </p>
                    </form>
                  )}

                  {/* ---------- otp form ---------- */}
                  {loginMode === "otp" && (
                    <form onSubmit={otpSent ? handleLogin : handleSendOtp}>
                      <div className="zd-field">
                        <label htmlFor="email">Email ID</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          placeholder="you@example.com"
                          required
                          value={name}
                          onChange={(e) => setUsername(e.target.value)}
                          disabled={otpSent}
                        />
                      </div>

                      {otpSent && (
                        <div className="zd-field">
                          <label htmlFor="otpInput">Enter OTP</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            className="form-control"
                            id="otpInput"
                            placeholder="6-digit code"
                            required
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <div className="zd-row-between mt-2">
                            <button
                              type="button"
                              className="zd-forgot"
                              disabled={otpTimer > 0 || submitting}
                              onClick={handleSendOtp}
                            >
                              {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : "Resend OTP"}
                            </button>
                            <button
                              type="button"
                              className="zd-forgot"
                              onClick={() => { setOtpSent(false); setOtp(''); }}
                            >
                              Change email
                            </button>
                          </div>
                        </div>
                      )}

                      <button type="submit" className="zd-submit" disabled={submitting}>
                        {submitting
                          ? (otpSent ? "Verifying..." : "Sending OTP...")
                          : (otpSent ? "Verify & Log In" : "Send OTP")}
                      </button>

                      <p className="zd-alt-action">
                        New to Zedock? <a href="" onClick={(e) => { e.preventDefault(); navigate("/SignUp") }}>Create an account</a>
                      </p>
                    </form>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Login;